import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../services/check_login';
import { motion, AnimatePresence } from 'framer-motion';
import { TbEdit, TbTrash, TbPlus, TbDeviceFloppy, TbLock, TbLockOpen } from 'react-icons/tb';
import '../../../assets/css/status-management.css';

const StatusManagement = () => {
  const { currentUser } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states
  const [currentStatus, setCurrentStatus] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#808080' });

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    fetchStatuses();
    fetchSettings();
  }, []);

  const fetchStatuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/statuses/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatuses(response.data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Error fetching statuses:', error);
      showNotification('Failed to fetch statuses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/statuses/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsEnabled(response.data.isEnabled);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleToggleSystem = async () => {
    if (!isEnabled && statuses.length < 2) {
      showNotification('You need at least 2 statuses to enable the status system', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/statuses/toggle', 
        { isEnabled: !isEnabled },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setIsEnabled(!isEnabled);
      showNotification(`Status system ${!isEnabled ? 'enabled' : 'disabled'} successfully`, 'success');
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to toggle status system', 'error');
    }
  };

  const handleCreateStatus = async () => {
    if (!formData.name.trim()) {
      showNotification('Status name is required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/statuses/create', 
        { ...formData, order: statuses.length },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showNotification('Status created successfully', 'success');
      setShowCreateModal(false);
      setFormData({ name: '', color: '#808080' });
      fetchStatuses();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to create status', 'error');
    }
  };

  const handleUpdateStatus = async () => {
    if (!formData.name.trim()) {
      showNotification('Status name is required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/statuses/${currentStatus._id}`, 
        formData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showNotification('Status updated successfully', 'success');
      setShowEditModal(false);
      setCurrentStatus(null);
      setFormData({ name: '', color: '#808080' });
      fetchStatuses();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to update status', 'error');
    }
  };

  const handleDeleteStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/statuses/${currentStatus._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Status deleted successfully', 'success');
      setShowDeleteModal(false);
      setCurrentStatus(null);
      fetchStatuses();
      fetchSettings(); // Re-fetch settings as system might auto-disable
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to delete status', 'error');
    }
  };

  const openEditModal = (status) => {
    setCurrentStatus(status);
    setFormData({ name: status.name, color: status.color });
    setShowEditModal(true);
  };

  const openDeleteModal = (status) => {
    setCurrentStatus(status);
    setShowDeleteModal(true);
  };

  const openCreateModal = () => {
    setFormData({ name: '', color: '#808080' });
    setShowCreateModal(true);
  };

  if (loading) {
    return <div className="status-loading">Loading statuses...</div>;
  }

  return (
    <div className="status-management-container">
      <div className="status-header">
        <div className="status-header-left">
          <h2>Status Management</h2>
          {isEnabled && <span className="system-enabled-badge">System Enabled</span>}
        </div>
        <div className="status-header-right">
          <button 
            className={`toggle-system-btn ${isEnabled ? 'enabled' : 'disabled'}`}
            onClick={handleToggleSystem}
            title={!isEnabled && statuses.length < 2 ? 'Need at least 2 statuses' : ''}
          >
            {isEnabled ? <><TbLockOpen /> Disable System</> : <><TbLock /> Enable System</>}
          </button>
          <button className="create-status-btn" onClick={openCreateModal}>
            <TbPlus /> Create
          </button>
        </div>
      </div>

      {statuses.length === 0 ? (
        <div className="no-statuses">
          <p>No statuses created yet. Create your first status to get started!</p>
        </div>
      ) : (
        <div className="status-cards-grid">
          {statuses.map((status) => (
            <motion.div 
              key={status._id} 
              className="status-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="status-card-header">
                <div className="status-name-with-color">
                  <div
                    className="status-color-preview"
                    style={{ backgroundColor: status.color }}
                  ></div>
                  <h3>{status.name}</h3>
                </div>
                <div className="status-card-actions">
                  <button
                    className="modify-status-btn"
                    onClick={() => openEditModal(status)}
                    title="Edit"
                  >
                    <TbEdit />
                  </button>
                  <button
                    className="remove-status-btn"
                    onClick={() => openDeleteModal(status)}
                    title="Delete"
                  >
                    <TbTrash />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Status Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <button 
                className="modal-close-button"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
              <h3>Create New Status</h3>
              <div className="form-group">
                <label>Status Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter status name"
                  className="status-input"
                />
              </div>
              <div className="form-group">
                <label>Color</label>
                <div className="color-picker-container">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="color-picker"
                  />
                  <span className="color-value">{formData.color}</span>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={handleCreateStatus}
                >
                  <TbDeviceFloppy /> Create
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Status Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <button 
                className="modal-close-button"
                onClick={() => {
                  setShowEditModal(false);
                  setCurrentStatus(null);
                }}
              >
                ✕
              </button>
              <h3>Edit Status</h3>
              <div className="form-group">
                <label>Status Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter status name"
                  className="status-input"
                />
              </div>
              <div className="form-group">
                <label>Color</label>
                <div className="color-picker-container">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="color-picker"
                  />
                  <span className="color-value">{formData.color}</span>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setShowEditModal(false);
                    setCurrentStatus(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={handleUpdateStatus}
                >
                  <TbDeviceFloppy /> Update
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Status Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <button 
                className="modal-close-button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setCurrentStatus(null);
                }}
              >
                ✕
              </button>
              <h3>Delete Status</h3>
              <div className="delete-warning-container">
                <p className="delete-warning-message">
                  Are you sure you want to delete the status <strong>"{currentStatus?.name}"</strong>?
                </p>
                <p className="delete-warning-note">
                  This action cannot be undone. If any creatives are using this status, the deletion will fail.
                </p>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCurrentStatus(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDeleteStatus}
                >
                  <TbTrash /> Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            className={`notification ${notification.type}`}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
          >
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StatusManagement;
